import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Undo2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import {
  traerBonos, guardarBono, editarBono, borrarBono, usarSesion, traerUsos, deshacerUso,
} from '../lib/pacientes';

/**
 * Los paquetes de sesiones de un paciente.
 *
 * QUÉ RESUELVE: hoy esto vive en un cuaderno, y el problema del cuaderno no es
 * llevarlo sino que la discusión sobre cuántas sesiones quedan aparece siempre
 * al final, cuando ya no hay forma de reconstruirlo.
 *
 * EL BOTÓN DE GASTAR SESIÓN SE APAGA MIENTRAS EL PEDIDO VIAJA. El servidor lo
 * resuelve en una transacción, así que dos toques seguidos descuentan una sola
 * vez de verdad; pero que el dato esté protegido no es razón para que la
 * pantalla se vea como si hubiera descontado dos. Lo que se está evitando acá no
 * es el error de datos —ese ya está cubierto— sino que la persona crea que pasó
 * algo que no pasó.
 */

const PAGO = {
  pagado: { texto: 'Pagado', clase: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
  debe: { texto: 'Debe', clase: 'border-red-200 bg-red-50 text-red-700' },
  // Tercer estado y no un "debe": un bono sin importe —de palabra, o de
  // cortesía— no es una deuda, y pintarlo de rojo junto a los que sí deben haría
  // que el rojo deje de significar algo.
  'sin-precio': { texto: 'Sin importe', clase: 'border-neutral-200 bg-neutral-50 text-neutral-500' },
};

const enPesos = (n) =>
  Number(n).toLocaleString('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 });

export default function BonosDePaciente({ currentUser, telefono, onError }) {
  const [bonos, setBonos] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [alta, setAlta] = useState(null);
  // Qué bono está esperando respuesta del servidor, para apagar su botón.
  const [ocupado, setOcupado] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const r = await traerBonos(currentUser, telefono);
      setBonos(r.bonos || []);
      setResumen(r.resumen || null);
    } catch (e) {
      onError?.(e.message);
    }
  }, [currentUser, telefono, onError]);

  useEffect(() => { cargar(); }, [cargar]);

  const conError = async (fn) => {
    try { await fn(); await cargar(); } catch (e) { onError?.(e.message); } finally { setOcupado(null); }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {resumen ? (
          <p className="m-0 text-sm text-neutral-600">
            <strong className="text-neutral-900">{resumen.sesionesRestantes}</strong> sesiones
            disponibles en {resumen.bonos} {resumen.bonos === 1 ? 'bono' : 'bonos'}
            {resumen.adeudados > 0 && (
              <> · <span className="font-semibold text-red-700">{enPesos(resumen.montoAdeudado)} sin pagar</span></>
            )}
          </p>
        ) : <span />}
        <Button type="button" size="sm" className="h-8 gap-1.5"
          onClick={() => setAlta({ nombre: '', sesionesTotales: 8, precio: '' })}>
          <Plus className="h-3.5 w-3.5" aria-hidden="true" />
          Cargar bono
        </Button>
      </div>

      {alta && (
        <form
          className="flex flex-wrap items-end gap-2 rounded-lg border border-neutral-200 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            conError(async () => {
              await guardarBono(currentUser, telefono, {
                ...alta,
                sesionesTotales: Number(alta.sesionesTotales),
                precio: alta.precio === '' ? undefined : Number(alta.precio),
              });
              setAlta(null);
            });
          }}
        >
          <label className="flex min-w-[10rem] flex-1 flex-col gap-1 text-sm">
            Nombre
            <Input value={alta.nombre} required placeholder="Paquete 8 sesiones"
              onChange={(e) => setAlta({ ...alta, nombre: e.target.value })} />
          </label>
          <label className="flex w-24 flex-col gap-1 text-sm">
            Sesiones
            <Input type="number" min={1} max={100} required value={alta.sesionesTotales}
              onChange={(e) => setAlta({ ...alta, sesionesTotales: e.target.value })} />
          </label>
          <label className="flex w-32 flex-col gap-1 text-sm">
            Precio <span className="text-[0.7rem] text-neutral-500">(opcional)</span>
            <Input type="number" min={0} value={alta.precio}
              onChange={(e) => setAlta({ ...alta, precio: e.target.value })} />
          </label>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => setAlta(null)}>Cancelar</Button>
            <Button type="submit">Guardar</Button>
          </div>
        </form>
      )}

      {bonos.length === 0 ? (
        <p className="m-0 py-8 text-center text-sm text-neutral-500">
          Todavía no hay bonos cargados.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Bono</TableHead>
                <TableHead className="text-center">Sesiones</TableHead>
                <TableHead>Pago</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bonos.map((b) => {
                const pago = PAGO[b.pago] || PAGO['sin-precio'];
                const agotado = b.restantes <= 0;
                return (
                  <TableRow key={b.id} className={b.activo === false ? 'opacity-50' : undefined}>
                    <TableCell>
                      <span className="block font-semibold text-neutral-900">{b.nombre}</span>
                      {b.precio > 0 && (
                        <span className="block text-[0.7rem] text-neutral-500">{enPesos(b.precio)}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center tabular-nums">
                      <strong className={agotado ? 'text-neutral-400' : 'text-neutral-900'}>
                        {b.restantes}
                      </strong>
                      <span className="text-neutral-400"> / {b.sesionesTotales}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={pago.clase}>{pago.texto}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex flex-wrap justify-end gap-1.5">
                        <Button
                          type="button" size="sm" variant="outline" className="h-8"
                          disabled={agotado || b.activo === false || ocupado === b.id}
                          title={agotado ? 'Ya no quedan sesiones en este bono' : undefined}
                          onClick={() => { setOcupado(b.id); conError(() => usarSesion(currentUser, telefono, b.id)); }}
                        >
                          {ocupado === b.id ? 'Guardando…' : 'Usó una sesión'}
                        </Button>
                        {b.sesionesUsadas > 0 && (
                          <Button
                            type="button" size="icon" variant="ghost"
                            className="h-8 w-8 text-neutral-400"
                            aria-label="Deshacer la última sesión usada"
                            title="Deshacer la última"
                            disabled={ocupado === b.id}
                            onClick={() => {
                              setOcupado(b.id);
                              conError(async () => {
                                /* Se pide el detalle en el momento en vez de
                                   guardarlo al cargar la tabla: entre que se
                                   pintó la lista y que alguien aprieta deshacer
                                   pudo haberse gastado otra sesión —desde otra
                                   pestaña, o desde el teléfono—, y borrar "el
                                   último que la pantalla recuerda" borraría el
                                   equivocado. La lista viene del más nuevo al
                                   más viejo. */
                                const { usos } = await traerUsos(currentUser, telefono, b.id);
                                if (!usos || usos.length === 0) {
                                  throw new Error('Este bono no tiene usos registrados.');
                                }
                                await deshacerUso(currentUser, telefono, b.id, usos[0].id);
                              });
                            }}
                          >
                            <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />
                          </Button>
                        )}
                        <Button
                          type="button" size="sm" variant="ghost" className="h-8"
                          disabled={ocupado === b.id}
                          onClick={() => conError(() => editarBono(currentUser, telefono, b.id, { pagado: !b.pagado }))}
                        >
                          {b.pagado ? 'Marcar impago' : 'Marcar pagado'}
                        </Button>
                        <Button
                          type="button" size="sm" variant="ghost"
                          className="h-8 text-neutral-400 hover:text-red-600"
                          onClick={() => {
                            if (!window.confirm(`¿Borrar "${b.nombre}" y su historial de usos?`)) return;
                            conError(() => borrarBono(currentUser, telefono, b.id));
                          }}
                        >
                          Borrar
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
